import * as React from 'react';
import { format, parse, isValid } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CalendarIcon, X } from 'lucide-react';
import { DateRange, DayPicker } from 'react-day-picker';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button, buttonVariants } from './button';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Input } from './input';
import { Calendar } from './calendar';

export interface DateRangeValue {
  from: Date | undefined;
  to: Date | undefined;
}

interface DateRangePickerProps {
  dateRange: DateRangeValue | undefined;
  onDateRangeChange: (range: DateRangeValue | undefined) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  highlightedDates?: Date[];
}

export function DateRangePicker({
  dateRange,
  onDateRangeChange,
  placeholder = 'Filtrar por data',
  className,
  disabled,
  highlightedDates = [],
}: DateRangePickerProps) {
  const [open, setOpen] = React.useState(false);
  const [isDragging, setIsDragging] = React.useState(false);
  const [dragStart, setDragStart] = React.useState<Date | null>(null);
  const [hoverDate, setHoverDate] = React.useState<Date | null>(null);

  // Create a Set of highlighted date strings for efficient lookup
  const highlightedDateSet = React.useMemo(() => {
    const set = new Set<string>();
    highlightedDates.forEach(date => {
      // Normalize to date string for comparison
      set.add(`${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`);
    });
    return set;
  }, [highlightedDates]);

  const isDateHighlighted = (date: Date): boolean => {
    return highlightedDateSet.has(`${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`);
  };

  // Text input states for manual date entry
  const [fromText, setFromText] = React.useState('');
  const [toText, setToText] = React.useState('');

  // Sync text inputs when dateRange changes externally or popover opens
  React.useEffect(() => {
    if (open) {
      setFromText(dateRange?.from ? format(dateRange.from, 'dd/MM/yyyy') : '');
      setToText(dateRange?.to ? format(dateRange.to, 'dd/MM/yyyy') : '');
    }
  }, [open, dateRange?.from?.getTime(), dateRange?.to?.getTime()]);

  const parseDate = (text: string): Date | undefined => {
    if (!text.trim()) return undefined;
    // Try parsing dd/MM/yyyy format
    const parsed = parse(text.trim(), 'dd/MM/yyyy', new Date());
    if (isValid(parsed) && parsed <= new Date()) {
      return parsed;
    }
    return undefined;
  };

  const handleFromTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFromText(value);

    const parsed = parseDate(value);
    if (parsed) {
      const newRange = { from: parsed, to: dateRange?.to };
      // Ensure from <= to
      if (newRange.to && parsed > newRange.to) {
        newRange.to = parsed;
        setToText(format(parsed, 'dd/MM/yyyy'));
      }
      onDateRangeChange(newRange);
    }
  };

  const handleToTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setToText(value);

    const parsed = parseDate(value);
    if (parsed && dateRange?.from) {
      const newRange = { from: dateRange.from, to: parsed };
      // Ensure to >= from
      if (parsed < dateRange.from) {
        newRange.from = parsed;
        setFromText(format(parsed, 'dd/MM/yyyy'));
      }
      onDateRangeChange(newRange);
    }
  };

  const formatDateRange = () => {
    if (!dateRange?.from) return placeholder;

    if (dateRange.to) {
      // Check if same day
      if (dateRange.from.toDateString() === dateRange.to.toDateString()) {
        return format(dateRange.from, 'dd/MM/yyyy', { locale: ptBR });
      }
      return `${format(dateRange.from, 'dd/MM', { locale: ptBR })} - ${format(dateRange.to, 'dd/MM/yyyy', { locale: ptBR })}`;
    }

    return format(dateRange.from, 'dd/MM/yyyy', { locale: ptBR });
  };

  const hasValue = dateRange?.from !== undefined;

  // Calculate preview range during drag
  const previewRange = React.useMemo(() => {
    if (isDragging && dragStart && hoverDate) {
      const start = dragStart < hoverDate ? dragStart : hoverDate;
      const end = dragStart < hoverDate ? hoverDate : dragStart;
      return { from: start, to: end };
    }
    return dateRange;
  }, [isDragging, dragStart, hoverDate, dateRange]);

  const handleDayPointerDown = (date: Date) => {
    if (date > new Date()) return; // Don't allow future dates
    setIsDragging(true);
    setDragStart(date);
    setHoverDate(date);
  };

  const handleDayPointerEnter = (date: Date) => {
    if (isDragging && date <= new Date()) {
      setHoverDate(date);
    }
  };

  const handlePointerUp = () => {
    if (isDragging && dragStart && hoverDate) {
      const start = dragStart < hoverDate ? dragStart : hoverDate;
      const end = dragStart < hoverDate ? hoverDate : dragStart;
      onDateRangeChange({ from: start, to: end });
      // Update text inputs
      setFromText(format(start, 'dd/MM/yyyy'));
      setToText(format(end, 'dd/MM/yyyy'));
      setOpen(false);
    }
    setIsDragging(false);
    setDragStart(null);
    setHoverDate(null);
  };

  // Add global pointerup listener to handle drag end outside calendar
  React.useEffect(() => {
    if (isDragging) {
      const handleGlobalPointerUp = () => handlePointerUp();
      document.addEventListener('pointerup', handleGlobalPointerUp);
      return () => document.removeEventListener('pointerup', handleGlobalPointerUp);
    }
  }, [isDragging, dragStart, hoverDate]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            'h-10 min-w-[200px] justify-start text-left font-normal gap-2',
            !hasValue && 'text-muted-foreground',
            hasValue && 'text-foreground',
            className
          )}
          disabled={disabled}
        >
          <CalendarIcon className="h-4 w-4 flex-shrink-0 opacity-50" />
          <span className="flex-1 truncate">{formatDateRange()}</span>
          {hasValue && (
            <X
              className="h-4 w-4 opacity-40 hover:opacity-100 flex-shrink-0 transition-opacity"
              onClick={(e) => {
                e.stopPropagation();
                onDateRangeChange(undefined);
              }}
            />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[276px] p-0" align="start" sideOffset={4} collisionPadding={8}>
        {/* Date input fields - hidden on mobile to prevent keyboard popup */}
        <div className="hidden sm:flex items-center gap-2 p-3">
          <div className="flex-1">
            <label className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-1 block">
              De
            </label>
            <Input
              type="text"
              placeholder="dd/mm/aaaa"
              value={fromText}
              onChange={handleFromTextChange}
              className="h-8 text-sm font-mono text-center"
            />
          </div>
          <div className="flex items-end pb-1">
            <span className="text-muted-foreground text-xs">→</span>
          </div>
          <div className="flex-1">
            <label className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-1 block">
              Até
            </label>
            <Input
              type="text"
              placeholder="dd/mm/aaaa"
              value={toText}
              onChange={handleToTextChange}
              className="h-8 text-sm font-mono text-center"
            />
          </div>
        </div>
        <div className="hidden sm:block border-t" />
        <div
          className="select-none touch-none"
          onPointerLeave={() => {
            if (isDragging) {
              // Keep the last hovered date
            }
          }}
        >
          <DayPicker
            locale={ptBR}
            mode="range"
            selected={previewRange as DateRange}
            onSelect={(range) => {
              if (!isDragging) {
                onDateRangeChange(range as DateRangeValue | undefined);
                // Update text inputs
                setFromText(range?.from ? format(range.from, 'dd/MM/yyyy') : '');
                setToText(range?.to ? format(range.to, 'dd/MM/yyyy') : '');
                if (range?.from && range?.to) {
                  setOpen(false);
                }
              }
            }}
            disabled={(date) => date > new Date()}
            numberOfMonths={1}
            showOutsideDays
            className="p-3 w-full"
            classNames={{
              months: 'flex flex-col w-full',
              month: 'space-y-3 w-full',
              month_caption: 'flex justify-center relative items-center h-9',
              caption_label: 'text-sm font-medium',
              nav: 'absolute inset-x-0 flex justify-between px-1',
              button_previous: cn(
                buttonVariants({ variant: 'ghost' }),
                'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100'
              ),
              button_next: cn(
                buttonVariants({ variant: 'ghost' }),
                'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100'
              ),
              month_grid: 'w-full border-collapse',
              weekdays: 'flex w-full',
              weekday: 'text-muted-foreground w-[36px] font-normal text-[0.8rem] text-center',
              week: 'flex w-full mt-1',
              day: cn(
                'h-[36px] w-[36px] text-center text-sm p-0 relative focus-within:relative focus-within:z-20',
                '[&:has([aria-selected].day-range-end)]:rounded-r-md',
                '[&:has([aria-selected].day-outside)]:bg-blue-100/50',
                '[&:has([aria-selected])]:bg-blue-100',
                'first:[&:has([aria-selected])]:rounded-l-md',
                'last:[&:has([aria-selected])]:rounded-r-md'
              ),
              day_button: cn(
                buttonVariants({ variant: 'ghost' }),
                'h-[36px] w-[36px] p-0 font-normal aria-selected:opacity-100 cursor-pointer'
              ),
              range_end: 'day-range-end',
              range_start: 'day-range-start',
              selected:
                'bg-blue-600 text-white hover:bg-blue-600 hover:text-white focus:bg-blue-600 focus:text-white rounded-md',
              today: 'bg-accent text-accent-foreground',
              outside:
                'day-outside text-muted-foreground aria-selected:bg-blue-50 aria-selected:text-muted-foreground',
              disabled: 'text-muted-foreground opacity-50 cursor-not-allowed',
              range_middle: 'aria-selected:bg-blue-100 aria-selected:text-blue-900',
              hidden: 'invisible',
            }}
            components={{
              Chevron: ({ orientation }) => {
                const Icon = orientation === 'left' ? ChevronLeft : ChevronRight;
                return <Icon className="h-4 w-4" />;
              },
              DayButton: ({ day, modifiers, ...props }) => {
                const date = day.date;
                const isDisabled = date > new Date();
                const hasQuotes = isDateHighlighted(date);
                const isSelected = modifiers.selected;
                return (
                  <button
                    {...props}
                    className={cn(
                      props.className,
                      // Add dot indicator for days with quotes (when not selected)
                      hasQuotes && !isSelected && 'relative after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:w-1 after:h-1 after:bg-green-500 after:rounded-full'
                    )}
                    onPointerDown={(e) => {
                      if (!isDisabled) {
                        e.preventDefault();
                        (e.target as HTMLElement).releasePointerCapture?.(e.pointerId);
                        handleDayPointerDown(date);
                      }
                    }}
                    onPointerEnter={() => handleDayPointerEnter(date)}
                    onPointerUp={handlePointerUp}
                  />
                );
              },
            }}
          />
        </div>
        <div className="flex items-center justify-between px-3 py-2 border-t bg-muted/30">
          <span className="text-[11px] text-muted-foreground">
            Arraste para selecionar período
          </span>
          {hasValue && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs hover:bg-destructive/10 hover:text-destructive"
              onClick={() => {
                onDateRangeChange(undefined);
                setFromText('');
                setToText('');
                setOpen(false);
              }}
            >
              <X className="h-3 w-3 mr-1" />
              Limpar
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Keep the old single date picker for backwards compatibility if needed
interface DatePickerProps {
  date: Date | undefined;
  onDateChange: (date: Date | undefined) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export function DatePicker({
  date,
  onDateChange,
  placeholder = 'Selecionar data',
  className,
  disabled,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            'w-[200px] justify-start text-left font-normal',
            !date && 'text-muted-foreground',
            className
          )}
          disabled={disabled}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {date ? format(date, 'dd/MM/yyyy', { locale: ptBR }) : placeholder}
          {date && (
            <X
              className="ml-auto h-4 w-4 opacity-50 hover:opacity-100"
              onClick={(e) => {
                e.stopPropagation();
                onDateChange(undefined);
              }}
            />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={date}
          onSelect={(selectedDate) => {
            onDateChange(selectedDate);
            setOpen(false);
          }}
          disabled={(date) => date > new Date()}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}
